import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Autocomplete,
  Typography,
  Skeleton,
  Stack,
  Button,
  Paper,
  Divider,
  Card,
  CardMedia,
  IconButton,
  Chip,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import SaveIcon from "@mui/icons-material/Save";
import { connect } from "react-redux";
import {
  fetchSinglePost,
  fetchPostCategory,
  fetchAllTopics,
  updatePost,
  deletePost,
  showAlert,
} from "../../../../redux";
import defaultTags from "./tags";
import GalleryWrapper from "./GalleryWrapper";
import {
  getSinglePost,
  getPostCategory,
  getPostActions,
} from "../../../../redux/post/postSelectors";
import { getTopics } from "../../../../redux/topic/topicSelectors";
import { requestState } from "../../../../redux/type/type";
import { useMarkdown } from "../../../../CustomeMarkdownEditor/providers/markdown-provider";
import { areObjectsEqual } from "../../../../Utils/stringFunctions.js";
import MDEditor from "@nmastepankaj/markdowneditor";
import { useTheme } from "@mui/material/styles";

const EditPost = (props) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const params = useParams();

  const {
    post,
    category,
    topics,
    postActions,
    fetchSinglePost,
    fetchPostCategory,
    fetchAllTopics,
    updatePost,
    deletePost,
    showAlert,
  } = props;

  const [catValue, setCatValue] = useState([]);
  const [topicValue, setTopicValue] = useState(null);
  const [topicOrder, setTopicOrder] = useState("");
  const [tag, setTag] = useState([]);
  const [postCatValue, setPostCatValue] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [postMarkdown, setPostMarkdown] = useMarkdown();
  const [open, setOpen] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const postFormat = {
    post_title: "",
    post_slug: "",
    post_description: "",
    post_markdown: "",
    post_category: [],
    post_topic: null,
    post_topic_order: null,
    post_meta_keywords: "",
    post_meta_description: "",
    post_publish: false,
    post_image: "",
    post_author: "",
  };

  const [value, setValue] = useState(postFormat);
  const [postValue, setPostValue] = useState(postFormat);

  const mediaBaseURL = process.env.REACT_APP_MEDIA_BASE_URL;

  // Fetch data on mount
  useEffect(() => {
    fetchSinglePost(params.postId);
    fetchPostCategory();
    fetchAllTopics();
  }, [params.postId]);

  // Set initial values when post loads
  useEffect(() => {
    if (post.post?.data) {
      const postData = post.post.data;
      setValue(postData);
      setPostValue(postData);

      const keywords = postData.post_meta_keywords;
      setTag(
        keywords && keywords.length > 0 ? keywords.split(", ") : []
      );
      setTopicOrder(postData.post_topic_order || "");
      setPostMarkdown(postData.post_markdown || "## Title");
    }
  }, [post, setPostMarkdown]);

  // Set category values
  useEffect(() => {
    if (category?.category) {
      setCatValue(category.category);
    }
  }, [category]);

  // Set selected categories and topic
  useEffect(() => {
    if (post.post?.data?.post_category && catValue.length > 0) {
      const selectedCategories = post.post.data.post_category
        .map((id) => catValue.find((cat) => cat.id === id))
        .filter(Boolean);
      setPostCatValue(selectedCategories);
    }

    if (post.post?.data?.post_topic && topics?.topics?.length > 0) {
      const selectedTopic = topics.topics.find(
        (topic) => topic.id === post.post.data.post_topic
      );
      setTopicValue(selectedTopic || null);
    }
  }, [catValue, topics, post]);

  // Check if form is valid
  useEffect(() => {
    if (!postValue) return;

    const catCompare = postCatValue.map((key) => key.id);
    const hasChanges =
      postValue.post_title !== value.post_title ||
      postValue.post_slug !== value.post_slug ||
      postValue.post_description !== value.post_description ||
      postValue.post_meta_description !== value.post_meta_description ||
      postValue.post_markdown !== postMarkdown ||
      !areObjectsEqual(postValue.post_category, catCompare) ||
      postValue.post_meta_keywords !== tag.join(", ");

    setIsValid(hasChanges);
  }, [value, postMarkdown, postCatValue, tag, postValue]);

  const handleImageModelOpen = () => setOpen(true);
  const handleImageModelClose = () => setOpen(false);

  const handleKeyDown = (event) => {
    if (event.key === ",") {
      event.preventDefault();
      event.stopPropagation();
      if (event.target.value.length > 0) {
        setTag([...tag, event.target.value.trim()]);
        setInputValue("");
      }
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    
    // Split by comma and filter out empty strings
    const newTags = pastedText
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
    
    if (newTags.length > 0) {
      setTag([...tag, ...newTags]);
      setInputValue("");
    }
  };

  const handleInputChange = (event, newInputValue) => {
    // Check if the input contains commas (for paste or manual entry)
    if (newInputValue.includes(',')) {
      const newTags = newInputValue
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      if (newTags.length > 0) {
        setTag([...tag, ...newTags]);
        setInputValue("");
      }
    } else {
      setInputValue(newInputValue);
    }
  };

  const onChange = (e) => {
    setValue({ ...value, [e.target.name]: e.target.value });
  };

  const handlePublish = async () => {
    try {
      const result = await updatePost({
        id: params.postId,
        value: { post_publish: !value.post_publish },
      });
      
      if (result.payload && result.payload.status_code === 200) {
        const status = !value.post_publish ? "published" : "unpublished";
        showAlert(`Post ${status} successfully`, "success");
      } else {
        showAlert("Failed to update post status", "error");
      }
    } catch (error) {
      showAlert(error.message || "An error occurred while updating post status", "error");
    }
  };

  const updateImage = async (id) => {
    try {
      const result = await updatePost({ id: params.postId, value: { post_image_id: id } });
      
      if (result.payload && result.payload.status_code === 200) {
        showAlert("Post image updated successfully", "success");
        handleImageModelClose();
      } else {
        showAlert("Failed to update post image", "error");
      }
    } catch (error) {
      showAlert(error.message || "An error occurred while updating post image", "error");
    }
  };

  const updatePostHandler = async (event) => {
    event.preventDefault();

    const updatedPost = {
      post_title: value.post_title,
      post_slug: value.post_slug,
      post_description: value.post_description,
      post_meta_description: value.post_meta_description,
      post_markdown: postMarkdown,
      post_category: postCatValue.map((key) => key.id),
      post_topic: topicValue?.id || null,
      post_topic_order: topicOrder ? parseInt(topicOrder) : null,
      post_meta_keywords: tag.join(", "),
    };

    try {
      const result = await updatePost({ id: params.postId, value: updatedPost });
      
      if (result.payload && result.payload.status_code === 200) {
        showAlert("Post updated successfully", "success");
        setPostValue(value); // Update the comparison value
      } else {
        showAlert("Failed to update post", "error");
      }
    } catch (error) {
      showAlert(error.message || "An error occurred while updating the post", "error");
    }
  };

  const deletePostHandler = async () => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      deletePost(params.postId).then((res) => {
        if (res.payload.status_code === 200) {
          navigate("/dashboard/post");
          showAlert("Post deleted successfully", "success");
        } else {
          showAlert("Failed to delete post", "error");
        }
      });
    }
  };

  // Loading state
  if (
    post.status !== requestState.success ||
    category.status !== requestState.success
  ) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Skeleton variant="text" sx={{ fontSize: "2rem" }} width="40%" />
          <Skeleton variant="rectangular" height={200} />
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={60} />
          <Skeleton variant="rectangular" height={300} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box component="form" onSubmit={updatePostHandler}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" gutterBottom>
              Edit Post
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Author: {value?.post_author}
              </Typography>
              <Chip
                label={value.post_publish ? "Published" : "Draft"}
                color={value.post_publish ? "success" : "default"}
                size="small"
              />
            </Box>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Post Image */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Featured Image
            </Typography>
            <Card sx={{ maxWidth: 500, position: "relative" }}>
              {value?.post_image ? (
                <CardMedia
                  component="img"
                  height="300"
                  image={mediaBaseURL + value.post_image}
                  alt="Post image"
                />
              ) : (
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "grey.200",
                  }}
                >
                  <Typography color="text.secondary">No image</Typography>
                </Box>
              )}
              <IconButton
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "background.paper" },
                }}
                onClick={handleImageModelOpen}
              >
                <EditIcon />
              </IconButton>
            </Card>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Basic Info */}
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <Stack spacing={3} sx={{ mb: 4 }}>
            <TextField
              label="Post Title"
              fullWidth
              required
              name="post_title"
              value={value?.post_title || ""}
              onChange={onChange}
            />

            <TextField
              label="Post Slug"
              fullWidth
              required
              name="post_slug"
              value={value?.post_slug || ""}
              onChange={onChange}
              helperText="URL-friendly version of the title"
            />

            <TextField
              label="Short Description"
              multiline
              rows={4}
              fullWidth
              name="post_description"
              value={value?.post_description || ""}
              onChange={onChange}
            />
          </Stack>

          <Divider sx={{ mb: 4 }} />

          {/* Categorization */}
          <Typography variant="h6" gutterBottom>
            Categorization
          </Typography>
          <Stack spacing={3} sx={{ mb: 4 }}>
            <Autocomplete
              multiple
              limitTags={2}
              options={catValue || []}
              getOptionLabel={(option) => option?.category_name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={postCatValue}
              onChange={(event, newValue) => setPostCatValue(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Categories" placeholder="Select categories" />
              )}
            />

            <Autocomplete
              options={topics?.topics || []}
              getOptionLabel={(option) => option?.topic_name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={topicValue}
              onChange={(event, newValue) => setTopicValue(newValue)}
              renderInput={(params) => (
                <TextField {...params} label="Topic (Optional)" placeholder="Select topic" />
              )}
            />

            {topicValue && (
              <TextField
                label="Topic Order"
                type="number"
                fullWidth
                value={topicOrder}
                onChange={(e) => setTopicOrder(e.target.value)}
                helperText="Lower numbers appear first in the topic series"
              />
            )}
          </Stack>

          <Divider sx={{ mb: 4 }} />

          {/* SEO */}
          <Typography variant="h6" gutterBottom>
            SEO Settings
          </Typography>
          <Stack spacing={3} sx={{ mb: 4 }}>
            <Autocomplete
              multiple
              freeSolo
              options={defaultTags}
              value={tag}
              onChange={(event, newValue) => setTag(newValue)}
              inputValue={inputValue}
              onInputChange={handleInputChange}
              renderInput={(params) => {
                params.inputProps.onKeyDown = handleKeyDown;
                params.inputProps.onPaste = handlePaste;
                return (
                  <TextField
                    {...params}
                    label="Meta Keywords"
                    placeholder="Enter keywords separated by commas"
                    helperText="Type keywords and press comma, or paste comma-separated keywords"
                  />
                );
              }}
            />

            <TextField
              label="Meta Description"
              multiline
              rows={3}
              fullWidth
              name="post_meta_description"
              value={value.post_meta_description || ""}
              onChange={onChange}
            />
          </Stack>

          <Divider sx={{ mb: 4 }} />

          {/* Content Editor */}
          <Typography variant="h6" gutterBottom>
            Post Content
          </Typography>
          <Box sx={{ mb: 4 }}>
            <div data-color-mode={isDarkMode ? "dark" : "light"}>
              <MDEditor
                value={postMarkdown}
                onChange={(value) => {
                  setPostMarkdown(value);
                }}
                height={500}
                theme={isDarkMode ? "dark" : "light"}
                imageBaseUrl={mediaBaseURL}
                galleryComponent={GalleryWrapper}
                onImageSelect={(imageUrl, imageTitle) => {
                  console.log('Image selected:', imageUrl, imageTitle);
                }}
              />
            </div>
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "space-between",
              flexWrap: "wrap",
              mt: 4,
            }}
          >
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={value.post_publish ? <UnpublishedIcon /> : <PublishIcon />}
                onClick={handlePublish}
                color={value.post_publish ? "warning" : "success"}
              >
                {value.post_publish ? "Unpublish" : "Publish"}
              </Button>

              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={deletePostHandler}
              >
                Delete
              </Button>
            </Box>

            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={!isValid || postActions.updatePost?.status === requestState.loading}
            >
              {postActions.updatePost?.status === requestState.loading ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Image Gallery Modal */}
      <Modal open={open} onClose={handleImageModelClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", md: 700 },
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Select Image
          </Typography>
          <GalleryWrapper height="400" handleClick={(postId) => updateImage(postId)} />
        </Box>
      </Modal>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    post: getSinglePost(state),
    category: getPostCategory(state),
    topics: getTopics(state),
    postActions: getPostActions(state),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchSinglePost: (id) => dispatch(fetchSinglePost(id)),
    fetchPostCategory: () => dispatch(fetchPostCategory()),
    fetchAllTopics: () => dispatch(fetchAllTopics()),
    updatePost: ({ id, value }) => dispatch(updatePost({ id, value })),
    deletePost: (id) => dispatch(deletePost(id)),
    showAlert: (message, type) => dispatch(showAlert(message, type)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(EditPost);
